import React, { FC } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import * as R from 'ramda'
import { cn } from 'design-system'

interface PaginationControlProps {
  page: number
  numPages: number
  onChange: (page: number) => void
  rangeToShow: number
  showFirstLast: boolean
  showPrevNext: boolean
  pageSize?: number
  setPageSize?: (pageSize: number) => void
  backgroundColor?: string
  className?: string
}

const calculatePagesOffset = (
  page: number,
  offsetSize: number,
  numPages: number,
  rangeToShow: number
) => {
  if (rangeToShow === -1) {
    return 0
  }
  if (page < offsetSize) {
    return 0
  }
  if (page + offsetSize >= numPages) {
    return Math.max(numPages - rangeToShow, 0)
  }
  return page - offsetSize
}

export const PaginationControl: FC<PaginationControlProps> = ({
  page,
  numPages,
  onChange,
  rangeToShow,
  showFirstLast,
  showPrevNext,
  pageSize,
  setPageSize,
  backgroundColor = 'bg-white',
  className
}) => {
  if (numPages <= 1 && pageSize && pageSize <= 10) {
    return null
  }

  const offsetSize = Math.floor(rangeToShow / 2)

  const pagesOffset = calculatePagesOffset(
    page,
    offsetSize,
    numPages,
    rangeToShow
  )

  const pageRange = R.range(
    pagesOffset,
    rangeToShow === -1
      ? numPages
      : Math.min(rangeToShow + pagesOffset, numPages)
  )

  const showEllipses = rangeToShow > 1 && numPages > rangeToShow

  return (
    <div className={cn('flex items-center gap-4', className)}>
      {numPages > 1 && (
        <ul
          className={cn(
            'flex h-10 items-center justify-between gap-1 rounded-lg border border-slate-200 px-4',
            backgroundColor
          )}>
          {showFirstLast ? (
            <li>
              <button
                disabled={page === 0}
                onClick={() => onChange(0)}
                className={cn('rounded-full p-2 text-base', {
                  'text-blue-600': page !== 0,
                  'cursor-not-allowed text-gray-500': page === 0
                })}>
                First
              </button>
            </li>
          ) : null}
          {showPrevNext ? (
            <li>
              <button
                onClick={() => onChange(page - 1)}
                disabled={page === 0}
                className={cn('flex items-center rounded-full p-2 text-base', {
                  'text-blue-600': page !== 0,
                  'cursor-not-allowed text-gray-500': page === 0
                })}>
                <ChevronLeft
                  aria-hidden
                  size={20}
                  className={cn({
                    'text-blue-600': page !== 0,
                    'cursor-not-allowed text-gray-500': page === 0
                  })}
                />
                Prev
              </button>
            </li>
          ) : null}
          {showEllipses && pagesOffset > 0 ? (
            <li className="size-8 text-center align-middle text-base">...</li>
          ) : null}
          {pageRange.map(listPage => (
            <li key={listPage}>
              <button
                onClick={() => onChange(listPage)}
                className={cn(
                  'border-x-0 border-b-2 border-t-0 px-2 text-base',
                  {
                    'border-b-blue-600 text-blue-600': page === listPage,
                    'border-b-transparent text-gray-500': page !== listPage
                  }
                )}>
                {listPage + 1}
              </button>
            </li>
          ))}
          {showEllipses && numPages > rangeToShow + pagesOffset ? (
            <li className="size-8 text-center align-middle text-base">...</li>
          ) : null}
          {showPrevNext ? (
            <li>
              <button
                onClick={() => onChange(page + 1)}
                disabled={page === numPages - 1}
                className={cn('flex items-center rounded-full p-2 text-base', {
                  'text-blue-600': page !== numPages - 1,
                  'cursor-not-allowed text-gray-500': page === numPages - 1
                })}>
                Next
                <ChevronRight
                  aria-hidden
                  size={20}
                  className={cn({
                    'text-blue-600': page !== numPages - 1,
                    'cursor-not-allowed text-gray-500': page === numPages - 1
                  })}
                />
              </button>
            </li>
          ) : null}
          {showFirstLast ? (
            <li>
              <button
                onClick={() => onChange(numPages - 1)}
                disabled={page === numPages - 1}
                className={cn('rounded-full p-2 text-base', {
                  'text-blue-600': page !== numPages - 1,
                  'cursor-not-allowed text-gray-500': page === numPages - 1
                })}>
                Last
              </button>
            </li>
          ) : null}
        </ul>
      )}

      {pageSize && setPageSize && (
        <select
          aria-label="Items per page"
          className={cn(
            'h-10 rounded-lg border border-solid border-slate-200 px-4 text-blue-600',
            backgroundColor
          )}
          value={pageSize}
          onChange={e => {
            setPageSize(Number(e.target.value))
            onChange(0)
          }}>
          {[10, 20, 30, 40, 50].map(sizeOption => (
            <option key={sizeOption} value={sizeOption}>
              Show {sizeOption}
            </option>
          ))}
        </select>
      )}
    </div>
  )
}
